import { zodResolver } from "@hookform/resolvers/zod";
import { isComgateProductCode } from "Components/Shared/Merchant/Products/AppProducts/constants";
import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useGetMerchantAppProductsQuery } from "Api/Queries/Merchants/useGetMerchantAppProductsQuery";
import { useDeletePartyMutation } from "Api/Mutations/Party/useDeletePartyMutation";
import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import {
  getPartyQueryKey,
  usePartyQuery,
} from "Api/Queries/Party/usePartyQuery";
import { useAddOrChangeRelatedParty } from "Components/Onboarding/BusinessEntity/Hooks/useRelatedParty";
import {
  type OwnerFormData,
  getOwnerModalSchema,
} from "Components/Onboarding/BusinessEntity/Steps/BeneficialOwnersStep/OwnerModal/schema";
import { addressDtoToValidationSchema } from "Schemas/addressSchemas";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { ValuesBox } from "Components/Onboarding/shared/ValuesBox";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { DateFormInput } from "Components/Shared/Inputs/Form/DateFormInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { CountryDropdown } from "Components/Shared/Inputs/Form/Dropdowns/CountryDropdown";
import { IdentificationCardDropdown } from "Components/Shared/Inputs/Form/Dropdowns/IdentificationCardDropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { AddressInput } from "Components/Shared/Inputs/Form/AddressInput";
import {
  AddressUncommittedProvider,
  useAddressUncommittedTracker,
} from "Components/Shared/Inputs/Form/AddressUncommittedContext";
import { useGenderList } from "Hooks/Options/useGenderList";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  AddressType,
  type AddressSuggestionDto,
  Gender,
  PartyType,
  RelationType,
  VatStatus,
} from "Infrastructure/Api/Api";
import { copyAddressDtoToFormFields } from "Utils/AddressUtils";
import { EMPTY_PHONE } from "Utils/PhoneUtils";
import { Resources, useResource } from "Translations/Resources";
import { endOfDay } from "date-fns";
import { useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

const COMPONENT_RESOURCES =
  Resources.Onboarding.BusinessOnboarding.Steps.BeneficialOwners.FormGroups;

const IDENTIFICATION_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form
    .PartyIdentification;

const DOCUMENTS_RESOURCES = Resources.Onboarding.Steps.Documents;

type Props = {
  publicID?: string;
  onAfterSubmit: () => void;
};

export const EditValuesForm = ({ publicID, onAfterSubmit }: Props) => {
  const genderList = useGenderList();
  const queryClient = useQueryClient();
  const { partyPublicID: userPartyPublicID } = useLoggedInUser() || {};
  const { t } = useResource();

  const { data: appProducts } = useGetMerchantAppProductsQuery();
  const hasComgateProduct = appProducts?.some((ap) =>
    isComgateProductCode(ap.productDto.code),
  );

  const formMethods = useForm<OwnerFormData>({
    resolver: zodResolver(getOwnerModalSchema(t)),
    defaultValues: {
      address: { type: AddressType.Permanent },
      gender: Gender.NotSet,
    },
  });
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { isDirty, errors },
  } = formMethods;

  const address = useWatch({ control, name: "address" });
  const { hasUncommittedAddress, uncommittedContextValue } =
    useAddressUncommittedTracker();

  const handleAcceptAddressSuggestion = (suggestion: AddressSuggestionDto) => {
    copyAddressDtoToFormFields(suggestion, (field, value) =>
      setValue(`address.${field}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    );
  };

  const handleClearAddress = () => {
    copyAddressDtoToFormFields({}, (field, value) =>
      setValue(`address.${field}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    );
  };

  const { data: party, isLoading } = usePartyQuery(publicID);

  const { mutate: updateParty, isPending: isUpdatingParty } = usePartyMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getPartyQueryKey(userPartyPublicID),
      });
      queryClient.invalidateQueries({
        queryKey: getPartyQueryKey(publicID),
      });
      onAfterSubmit();
    },
  });
  const { mutate: deleteParty, isPending: isDeletingParty } =
    useDeletePartyMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getPartyQueryKey(userPartyPublicID),
        });
        onAfterSubmit();
      },
    });

  const { isPending, updateRelatedParty } = useAddOrChangeRelatedParty({
    owningPartyPublicID: publicID,
    onSuccessUpdate: () => {
      queryClient.invalidateQueries({
        queryKey: getPartyQueryKey(userPartyPublicID),
      });
      queryClient.invalidateQueries({
        queryKey: getPartyQueryKey(publicID),
      });
      onAfterSubmit();
    },
  });

  useEffect(() => {
    if (publicID && party) {
      const address = party?.addresses?.find(
        ({ type }) => type === AddressType.Permanent,
      );
      reset({
        address: addressDtoToValidationSchema(address) ?? {
          type: AddressType.Permanent,
        },
        birthDate: party.birthDate
          ? endOfDay(new Date(party.birthDate)) // if we receive date with 00:00:00, the calendar will show the previous day
          : undefined,
        firstName: party.firstName ?? undefined,
        lastName: party.lastName ?? undefined,
        gender: party.gender,
        nationality: party.nationality ?? undefined,
        partyIdentification: party.partyIdentification ?? undefined,
      });
    }
  }, [party, reset, publicID]);

  const onSubmit = async ({
    firstName,
    lastName,
    birthDate,
    nationality,
    gender,
    address,
    partyIdentification,
  }: OwnerFormData) => {
    if (hasUncommittedAddress) {
      toast.error(t(Resources.Components.Shared.AddressInput.UncommittedQuery));
      return;
    }

    const otherAddresses =
      party?.addresses?.filter(({ type }) => type !== AddressType.Permanent) ||
      [];

    const updatedParty = {
      ...(party ?? {}),
      vatNumber: party?.vatNumber ?? "", // carried through from the DTO; empty for a natural person
      firstName,
      lastName,
      gender,
      birthDate: new Date(birthDate).toISOString(),
      nationality,
      // The owner form offers no second citizenship; keep whatever the party already holds.
      otherNationalities: party?.otherNationalities ?? [],
      type: PartyType.NaturalPerson,
      isForeigner: party?.isForeigner ?? false, //TODO? false by default doesn't mean they're not foreigner
      relatedParties: party?.relatedParties || [],
      addresses: [
        { ...address, type: AddressType.Permanent },
        ...otherAddresses,
      ],
      hasIdentificationsUploaded: party?.hasIdentificationsUploaded,
      licenses: party?.licenses || [],
      partyIdentification: partyIdentification ?? party?.partyIdentification,
      phone: party?.phone ?? EMPTY_PHONE,
      // Required by the DTO shape only — the server ignores them on updates.
      verifiedPhone: party?.verifiedPhone ?? EMPTY_PHONE,
      vatStatus: party?.vatStatus ?? VatStatus.Unknown,
    };

    if (publicID === undefined) {
      await updateRelatedParty({
        relationType: RelationType.BeneficialOwner,
        relatedPartyData: updatedParty,
      });
    } else {
      updateParty({ publicID, data: updatedParty });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <AddressUncommittedProvider value={uncommittedContextValue}>
        <FullHeightForm onSubmit={handleSubmit(onSubmit)}>
          <StepBox
            submitButtonLabel={t(Resources.Common.Save)}
            submitDisabled={!isDirty}
            isSubmitting={
              isPending || isLoading || isUpdatingParty || isDeletingParty
            }
          >
            <ValuesBox
              title={t(COMPONENT_RESOURCES.BasicInformation)}
              hideDivider
            >
              <FormInput
                control={control}
                name="firstName"
                label={t(Resources.Common.FirstName)}
              />
              <FormInput
                control={control}
                name="lastName"
                label={t(Resources.Common.LastName)}
              />
              <Dropdown
                control={control}
                name="gender"
                label={t(Resources.Common.Gender)}
                codeList={genderList}
              />
              <DateFormInput
                control={control}
                name="birthDate"
                label={t(Resources.Common.BirthDate)}
              />
              <CountryDropdown
                control={control}
                name="nationality"
                label={t(Resources.Common.Nationality)}
              />
            </ValuesBox>
            <ValuesBox title={t(COMPONENT_RESOURCES.Address)} hideDivider>
              <AddressInput
                address={address}
                onAcceptSuggestion={handleAcceptAddressSuggestion}
                onClear={handleClearAddress}
                hasErrors={!!errors.address}
              >
                <FormInput
                  control={control}
                  name="address.street"
                  label={t(Resources.Common.Address.Street)}
                />
                <Grid container spacing={3}>
                  <Grid size={{ xs: 6 }}>
                    <FormInput
                      control={control}
                      name="address.streetNumber"
                      label={t(Resources.Common.Address.StreetNumber)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <FormInput
                      control={control}
                      name="address.orientationNumber"
                      label={t(Resources.Common.Address.OrientationNumber)}
                    />
                  </Grid>
                </Grid>
                <FormInput
                  control={control}
                  name="address.municipality"
                  label={t(Resources.Common.Address.Municipality)}
                />
                <FormInput
                  control={control}
                  name="address.postalCode"
                  label={t(Resources.Common.Address.PostalCode)}
                  mask="000 00"
                />
              </AddressInput>
            </ValuesBox>
            {hasComgateProduct && (
              <>
                <ValuesBox
                  title={t(DOCUMENTS_RESOURCES.FirstDocumentTitle)}
                  hideDivider
                >
                  <IdentificationCardDropdown
                    control={control}
                    name="partyIdentification.identification"
                    label={t(IDENTIFICATION_RESOURCES.Identification)}
                  />
                  <FormInput
                    control={control}
                    name="partyIdentification.identificationNumber"
                    label={t(IDENTIFICATION_RESOURCES.IdentificationNumber)}
                  />
                  <DateFormInput
                    control={control}
                    name="partyIdentification.identificationValidFrom"
                    label={t(IDENTIFICATION_RESOURCES.IdentificationValidFrom)}
                  />
                  <DateFormInput
                    control={control}
                    name="partyIdentification.identificationValidTo"
                    label={t(IDENTIFICATION_RESOURCES.IdentificationValidTo)}
                  />
                  <FormInput
                    control={control}
                    name="partyIdentification.identificationIssuer"
                    label={t(IDENTIFICATION_RESOURCES.IdentificationIssuer)}
                  />
                </ValuesBox>
                <ValuesBox
                  title={t(DOCUMENTS_RESOURCES.SecondDocumentTitle)}
                  hideDivider
                >
                  <IdentificationCardDropdown
                    control={control}
                    name="partyIdentification.secondIdentification"
                    label={t(IDENTIFICATION_RESOURCES.Identification)}
                  />
                  <FormInput
                    control={control}
                    name="partyIdentification.secondIdentificationNumber"
                    label={t(IDENTIFICATION_RESOURCES.IdentificationNumber)}
                  />
                  <DateFormInput
                    control={control}
                    name="partyIdentification.secondIdentificationValidFrom"
                    label={t(IDENTIFICATION_RESOURCES.IdentificationValidFrom)}
                  />
                  <DateFormInput
                    control={control}
                    name="partyIdentification.secondIdentificationValidTo"
                    label={t(IDENTIFICATION_RESOURCES.IdentificationValidTo)}
                  />
                  <FormInput
                    control={control}
                    name="partyIdentification.secondIdentificationIssuer"
                    label={t(IDENTIFICATION_RESOURCES.IdentificationIssuer)}
                  />
                </ValuesBox>
              </>
            )}
          </StepBox>
          {publicID && (
            <Box marginTop={3}>
              <PrimaryButton
                fullWidth
                onClick={() => deleteParty(publicID)}
                color="error"
                variant="outlined"
                isLoading={isDeletingParty}
              >
                {t(Resources.Common.Delete)}
              </PrimaryButton>
            </Box>
          )}
        </FullHeightForm>
      </AddressUncommittedProvider>
    </FormProvider>
  );
};
